import AddIcon from '@mui/icons-material/Add';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { useGroupRules, UserGroupRules } from 'src/graphql';
import {
  ConversationAssigneeGroupManagement,
  useConversationAssigneeGroupsManagement,
} from 'src/graphql/entities/conversation-assignee-group';
import { Modals, useModalMethods } from 'src/store/modals';
import { useNotifications } from 'src/utils/hooks';
import { AssigneeGroupsTable } from './assignee-groups-table';
import { sortPlatformFirstByName } from './utils/sort-assignee-groups';

interface AssigneeGroupsViewProps {
  partnerId: number | null;
}

export const AssigneeGroupsView = ({ partnerId }: AssigneeGroupsViewProps) => {
  const { checkValidity } = useGroupRules();
  const canManage = checkValidity([UserGroupRules.AccessToManageAssigneeGroups]);

  const { showErrorAlert } = useNotifications();
  const { groups, loading, error } = useConversationAssigneeGroupsManagement(partnerId);
  const { showModal } = useModalMethods(Modals.ManageAssigneeGroup);

  const [reportedError, setReportedError] = useState<unknown>(null);

  useEffect(() => {
    if (error && error !== reportedError) {
      setReportedError(error);
      showErrorAlert(
        error.message || 'Failed to load conversation assignee groups. Please try again.',
      );
    }
  }, [error, reportedError, showErrorAlert]);

  const sortedGroups = useMemo(() => sortPlatformFirstByName(groups ?? []), [groups]);

  if (partnerId === null) {
    return (
      <Box py={4} textAlign="center">
        <Typography variant="body2" color="text.secondary">
          Select a partner to manage assignee groups.
        </Typography>
      </Box>
    );
  }

  const isPlatformContext = partnerId === PLATFORM_PARTNER_ID;
  const helperCopy = isPlatformContext
    ? 'Manage platform-level assignee groups. Changes apply to every partner.'
    : 'Manage conversation assignee groups for this partner. Platform groups are read-only.';

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" color="text.secondary">
          {helperCopy}
        </Typography>
        {canManage ? (
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            data-testid="assignee-groups-add"
            onClick={() => showModal({ mode: 'create', partnerId })}
          >
            Add group
          </Button>
        ) : null}
      </Stack>

      {loading && !sortedGroups.length ? (
        <Box py={6} display="flex" justifyContent="center">
          <CircularProgress size={24} />
        </Box>
      ) : (
        <AssigneeGroupsTable
          groups={sortedGroups}
          partnerId={partnerId}
          canEditPlatformGroups={isPlatformContext}
          onEdit={(group: ConversationAssigneeGroupManagement) =>
            showModal({ mode: 'edit', partnerId, group })
          }
        />
      )}
    </Stack>
  );
};
